import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { userDataService } from './userDataService';

export interface ActivityLeadModalData {
  // Activity related data
  activityData?: any;
  editMode?: boolean;
  createActivity?: boolean;
  
  // Lead related data
  leadData?: any;
  leadId?: string | number;
  
  // Modal configuration
  primaryView?: 'activity' | 'lead';
  width?: string;
  height?: string;
  
  // Additional options
  hourSegments?: number;
  detailed?: boolean;
  
  // URL management
  clearUrlOnClose?: boolean;
  urlParamToClear?: string;
  
  // Navigation support
  lastActivityRequest?: any;
  isActivityView?: boolean;
  activityTypes?: any[];
}

@Injectable({
  providedIn: 'root'
})
export class ActivityLeadService {
  
  constructor(
    private dialog: MatDialog,
    private router: Router,
    private route: ActivatedRoute,
    private userDataService:userDataService
  ) {}

  /**
   * Clear specific query parameter from URL
   */
  private clearUrlParameter(paramName: string) {
    const queryParams = { ...this.route.snapshot.queryParams };
    delete queryParams[paramName];
    
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'replace'
    });
  }

  /**
   * Lazy load EventConfirmationDialogComponent
   */
  private async loadEventConfirmationDialog() {
    const { EventConfirmationDialogComponent } = await import('../shared/calendar/event-confirmation-dialog/event-confirmation-dialog.component');
    return EventConfirmationDialogComponent;
  }

  private async loadActivityLeadDialog() {
    const { ActivityLeadManagementComponent } = await import('../shared/calendar/activity-lead-management/activity-lead-management.component');
    return ActivityLeadManagementComponent;
  }

  /**
   * Open the unified Activity + Lead management modal
   */
  async openActivityLeadModal(config: ActivityLeadModalData) {
    const ActivityLeadManagementComponent = await this.loadActivityLeadDialog();
    const dialogRef = this.dialog.open(ActivityLeadManagementComponent, {
      position: { bottom: '0', right: '0' },
      height: '95vh',
      width: '80%',
      maxWidth:'1500px',
      panelClass: 'right-side-dialog',
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '300ms',
      hasBackdrop: true,
      disableClose: false,
      autoFocus: false,
      data: {
        ...config,
        // Set defaults
        hourSegments: config.hourSegments || 30,
        detailed: config.detailed !== false, // Default to true unless explicitly false
        // Pass navigation data
        lastActivityRequest: config.lastActivityRequest,
        isActivityView: config.isActivityView || false,
        activityTypes: config.activityTypes || []
      }
    });

    // Handle URL clearing on modal close
    if (config.clearUrlOnClose && config.urlParamToClear) {
      dialogRef.afterClosed().subscribe(() => {
        this.clearUrlParameter(config.urlParamToClear);
      });
    }

    return dialogRef;
  }

  /**
   * Open modal primarily for activity management (with lead context)
   */
  openForActivity(activityData: any, leadData?: any) {
    return this.openActivityLeadModal({
      activityData,
      leadData,
      primaryView: 'activity',
      editMode: !!activityData?.id,
      clearUrlOnClose: true,
      urlParamToClear: 'activity_id',
      lastActivityRequest: activityData?.lastActivityRequest,
      isActivityView: activityData?.isActivityView,
      activityTypes: activityData?.activityTypes
    });
  }

  /**
   * Open modal primarily for lead management (with activity creation option)
   */
  openForLead(leadData: any, createActivity = false) {
    return this.openActivityLeadModal({
      leadData,
      primaryView: 'lead',
      createActivity
    });
  }

  /**
   * Open modal for creating new activity for a specific lead
   */
  openForNewActivity(leadId: string | number, leadData?: any) {
    return this.openActivityLeadModal({
      leadId,
      leadData,
      primaryView: 'activity',
      createActivity: true,
      editMode: false
    });
  }

  /**
   * Open modal for editing existing activity with lead context
   */
  openForEditActivity(activityData: any, leadData?: any) {
    const dialogData = {
        activityData: {
            ...activityData,
            editMode: true,
            detailed: true,
            isExternal: true,
            isModal: true
        },
        leadData,
        primaryView: 'activity' as const,
        editMode: true,
        clearUrlOnClose: true,
        urlParamToClear: 'activity_id',
        lastActivityRequest: activityData?.lastActivityRequest,
        isActivityView: activityData?.isActivityView,
        activityTypes: activityData?.activityTypes,
        hourSegments: activityData?.hourSegments
    };
    
    return this.openActivityLeadModal(dialogData);
  }

  /**
   * Replace existing EventConfirmationDialog calls - for appointments
   */
  openAppointment(appointmentData: any) {
    return this.openActivityLeadModal({
      activityData: {
        ...appointmentData,
        editMode: true,
        detailed: true,
        isExternal: true,
        isModal: true,
        task: false
      },
      primaryView: 'activity',
      editMode: true,
      width: '90vw',
      height: '95vh',
      clearUrlOnClose: true,
      urlParamToClear: 'activity_id',
      lastActivityRequest: appointmentData?.lastActivityRequest,
      isActivityView: appointmentData?.isActivityView,
      activityTypes: appointmentData?.activityTypes,
      hourSegments: appointmentData?.hourSegments
    });
  }

  /**
   * Replace existing EventConfirmationDialog calls - for tasks
   */
  openTask(taskData: any) {
    return this.openActivityLeadModal({
      activityData: {
        ...taskData,
        editMode: !!taskData?.id,
        detailed: true,
        isExternal: true,
        isModal: true
      },
      primaryView: 'activity',
      editMode: !!taskData?.id,
      clearUrlOnClose: true,
      urlParamToClear: 'activity_id',
      lastActivityRequest: taskData?.lastActivityRequest,
      isActivityView: taskData?.isActivityView,
      activityTypes: taskData?.activityTypes,
      hourSegments: taskData?.hourSegments
    });
  }

  /**
   * Open activity-only modal with lazy loading
   */
  async openActivityOnlyModal(data: any) {
    try {
      const EventConfirmationDialogComponent = await this.loadEventConfirmationDialog();
      
      const dialogRef = this.dialog.open(EventConfirmationDialogComponent, {
        position: { top: '0' },
        height: '95%',
        maxHeight: '900px',
        width: '60%',
        minWidth: '400px',
        maxWidth: '800px',
        enterAnimationDuration: '150ms',
        exitAnimationDuration: '100ms',
        panelClass: 'activity-dialog',
        data: {...data},
      });

      // Clear URL parameter when this modal closes
      dialogRef.afterClosed().subscribe(() => {
        this.clearUrlParameter('activity_id');
      });

      return dialogRef
    } catch (error) {
      console.error('Failed to load EventConfirmationDialogComponent:', error);
      throw error;
    }
  }


  async openFullScreanActivity(data: any) {
    
    const enhancedData = {
      ...data,
      lastActivityRequest: data.lastActivityRequest,
      isActivityView: data.isActivityView || false,
      activityTypes: data.activityTypes || [],
      hourSegments: data.hourSegments || 30,
  
      isFromEngagementHub: data.isFromEngagementHub,
      engagementAction: data.engagementAction
    };
  
    return this.openForEditActivity(enhancedData);
  }



  /**
 * Open activity with lazy loading - enhanced with navigation support
 */
async openActivity(data: any) {
  let user = await this.userDataService.getUserData();
  
  // Get viewMode from localStorage
  const viewMode = user?.preferred_view_mode || 'fullscreen';
  
  const enhancedData = {
    ...data,
    lastActivityRequest: data.lastActivityRequest,
    isActivityView: data.isActivityView || false,
    activityTypes: data.activityTypes || [],
    hourSegments: data.hourSegments || 30,

    isFromEngagementHub: data.isFromEngagementHub,
    engagementAction: data.engagementAction
  };

  if (viewMode === 'fullscreen') {
    return this.openForEditActivity(enhancedData);
  } else {
    return this.openActivityOnlyModal(enhancedData);
  }
}
}